import type {
    CreateExtensionArgument,
    CreateExtArgs,
    Extension,
    ExtensionAPI,
    DmApis,
    ExtensionPublicAPI
} from '@wix/document-manager-core'
import type {BuilderStyle, PossibleStyleForBuilderComponent, CompRef, Pointer} from '@wix/document-services-types'
import {DATA_TYPES} from '../../constants/constants'
import type {BuilderComponentsExtensionAPI} from './builderComponents'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {StylesExtensionApi} from '../style/style'
import type {CssStyleExtensionAPI} from '../cssStyle/cssStyle'

export interface BuilderStylePublicAPI extends ExtensionPublicAPI {
    components: {
        cssStyle: {
            update(
                componentPointer: CompRef,
                cssStyle: Partial<BuilderStyle>,
                shouldRemoveRelatedProperties?: boolean
            ): void
            get(componentPointer: CompRef): PossibleStyleForBuilderComponent
            remove(componentPointer: CompRef, paths: string[]): void
            connectToGlobalStyle(componentPointer: CompRef, globalStylePointerOrId: Pointer | string): void
        }
    }
}

const createExtension = ({}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = ({extensionAPI}: CreateExtArgs): BuilderStyleExtensionAPI => {
        const {builderComponents, variants, style, cssStyle} = extensionAPI as BuilderComponentsExtensionAPI &
            VariantsExtensionAPI &
            StylesExtensionApi &
            CssStyleExtensionAPI

        const getCSSStyle: BuilderComponentStyleAPI['getCSSStyle'] = (componentPointer: CompRef) => {
            return cssStyle.components.getCSSStyleByPointer(componentPointer)
        }

        const buildDefaultStyleItem = (compPointer: CompRef): BuilderStyle => {
            const {style: componentStyleType} = builderComponents.getBuilderTypes(compPointer)
            return {
                type: componentStyleType
            }
        }

        const getCSSStyleAndResolveGlobalStyle = (componentPointer: CompRef): BuilderStyle | undefined => {
            let styleItem = cssStyle.components.getCSSStyleByPointer(componentPointer)

            if (styleItem?.type === 'GlobalStyle') {
                const globalStylePointer = style.global.getPointer(styleItem.id)
                styleItem = style.global.get(globalStylePointer) as BuilderStyle

                // eventually, we will want to inherit the variants - but we will also want to fallback to the non-variant value when getting the value:
                // const globalStylePointer = pointers.variants.inheritVariantsFromPointer(
                //     style.global.getPointer(styleItem.id),
                //     componentPointer
                // )
                // style.global.getWithFallbacks(globalStylePointer)
            }
            return styleItem
        }
        const updateCSSStyle: BuilderComponentStyleAPI['updateCSSStyle'] = (
            componentPointer: CompRef,
            partialStyle: Partial<BuilderStyle>,
            shouldRemoveRelatedProperties = true
        ): void => {
            const styleItem =
                getCSSStyleAndResolveGlobalStyle(componentPointer) ?? buildDefaultStyleItem(componentPointer)
            const newStyleItem = cssStyle.getUpdatedCSSStyleItemForUpdate(
                styleItem,
                partialStyle,
                shouldRemoveRelatedProperties
            )

            variants.updateComponentDataConsideringVariants(componentPointer, newStyleItem, DATA_TYPES.theme)
        }

        const removeCSSStyleProperties: BuilderComponentStyleAPI['removeCSSStyleProperties'] = (
            componentPointer: CompRef,
            paths: string[]
        ): void => {
            const styleItem = getCSSStyleAndResolveGlobalStyle(componentPointer)
            if (!styleItem) return

            const itemAfterRemovalUpdates = cssStyle.getUpdatedCSSStyleItemForRemove(styleItem, paths)
            if (itemAfterRemovalUpdates) {
                variants.updateComponentDataConsideringVariants(
                    componentPointer,
                    itemAfterRemovalUpdates,
                    DATA_TYPES.theme
                )
            }
        }

        const connectToGlobalStyle = (componentPointer: CompRef, globalStylePointerOrId: Pointer | string) => {
            const globalStyleDefinition = style.global.definition.get(globalStylePointerOrId)
            const {style: componentStyleType} = builderComponents.getBuilderTypes(componentPointer)
            if (globalStyleDefinition.styleType !== componentStyleType) {
                throw new Error(
                    `The style type ${globalStyleDefinition.styleType} is not derived from the component manifest. The component type is ${componentStyleType}`
                )
            }
            style.connectToGlobalStyle(componentPointer, globalStylePointerOrId)
        }

        return {
            builderStyle: {
                component: {
                    getCSSStyle,
                    updateCSSStyle,
                    removeCSSStyleProperties,
                    connectToGlobalStyle
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis): BuilderStylePublicAPI => {
        const {builderStyle} = extensionAPI as BuilderStyleExtensionAPI

        const update = (
            componentPointer: CompRef,
            cssStyle: BuilderStyle,
            shouldRemoveRelatedProperties?: boolean
        ): void => {
            builderStyle.component.updateCSSStyle(componentPointer, cssStyle, shouldRemoveRelatedProperties)
        }

        const get = (componentPointer: CompRef) => {
            return builderStyle.component.getCSSStyle(componentPointer)
        }

        const remove = (componentPointer: CompRef, paths: string[]) => {
            builderStyle.component.removeCSSStyleProperties(componentPointer, paths)
        }

        const connectToGlobalStyle = (componentPointer: CompRef, globalStylePointerOrId: Pointer | string) => {
            builderStyle.component.connectToGlobalStyle(componentPointer, globalStylePointerOrId)
        }

        return {
            components: {
                cssStyle: {
                    update,
                    get,
                    remove,
                    connectToGlobalStyle
                }
            }
        }
    }

    return {
        name: 'builderStyle',
        dependencies: new Set(['variants', 'builderComponents', 'cssStyle']),
        createExtensionAPI,
        createPublicAPI
    }
}

export interface BuilderComponentStyleAPI extends ExtensionAPI {
    connectToGlobalStyle(componentPointer: CompRef, globalStylePointerOrId: Pointer | string): void
    getCSSStyle(componentPointer: CompRef): PossibleStyleForBuilderComponent
    updateCSSStyle(
        componentPointer: CompRef,
        partialStyle: Partial<BuilderStyle>,
        shouldRemoveRelatedProperties?: boolean
    ): void
    removeCSSStyleProperties(componentPointer: CompRef, paths: string[]): void
}

export interface BuilderStyleExtensionAPI extends ExtensionAPI {
    builderStyle: {
        component: BuilderComponentStyleAPI
    }
}

export {createExtension}
